import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { getAuthHeader, parseResponse } from '../api/helpers';
import { C, cardStyle } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.btrix.in';

const STATS_CONFIG = [
  { label: 'Total Patients',     key: 'patients',      grad: 'linear-gradient(135deg,#6366f1,#8b5cf6)', icon: '👥' },
  { label: 'Notifications Sent', key: 'notifications', grad: 'linear-gradient(135deg,#8b5cf6,#ec4899)', icon: '💬' },
  { label: 'Appointments Today', key: 'appointments',  grad: 'linear-gradient(135deg,#f59e0b,#f97316)', icon: '📅' },
  { label: 'OP Sheets Scanned',  key: 'opsheets',      grad: 'linear-gradient(135deg,#0ea5e9,#6366f1)', icon: '📋' },
];

function StatCard({ label, value, grad, icon, loading }) {
  return (
    <div className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: grad, borderRadius: '12px 12px 0 0',
      }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 11, background: grad,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
        }}>{icon}</div>
      </div>
      <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4, lineHeight: 1 }}>
        {loading ? <span style={{ color: '#334155' }}>—</span> : value}
      </div>
      <div style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>{label}</div>
    </div>
  );
}

const STATUS_BADGE = {
  sent:    { bg: 'rgba(34,197,94,0.15)',  text: '#4ade80' },
  pending: { bg: 'rgba(234,179,8,0.15)',  text: '#fbbf24' },
  failed:  { bg: 'rgba(239,68,68,0.15)',  text: '#f87171' },
};

function NotificationRow({ item }) {
  const c = STATUS_BADGE[item.status] || STATUS_BADGE.pending;
  return (
    <tr>
      <td style={{ padding: '11px 16px', fontSize: 12, fontWeight: 600, color: 'var(--accent-light)', fontFamily: 'monospace', borderBottom: '1px solid var(--row-border)' }}>{item.patient_phone}</td>
      <td style={{ padding: '11px 16px', fontSize: 12, color: '#94a3b8', borderBottom: '1px solid var(--row-border)', textTransform: 'capitalize' }}>{item.type?.replace(/_/g,' ') || 'notification'}</td>
      <td style={{ padding: '11px 16px', fontSize: 11, color: '#475569', borderBottom: '1px solid var(--row-border)' }}>{item.scheduled_time?.slice(0, 16).replace('T', ' ')}</td>
      <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--row-border)' }}>
        <span style={{ background: c.bg, color: c.text, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{item.status}</span>
      </td>
    </tr>
  );
}

export default function Dashboard({ user }) {
  const HOSPITAL_ID = user?.hospital_id;
  const [stats, setStats]                 = useState({ patients: 0, notifications: 0, appointments: 0, opsheets: 0 });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const navigate                          = useNavigate();

  const fetchData = useCallback(async () => {
    const todayStr = new Date().toISOString().slice(0, 10);
    try {
      const headers = await getAuthHeader();
      const [patientsRes, notifsRes, apptsRes] = await Promise.all([
        axios.get(`${API_URL}/patients`,      { params: { hospital_id: HOSPITAL_ID }, headers }),
        axios.get(`${API_URL}/notifications`, { params: { hospital_id: HOSPITAL_ID }, headers }),
        axios.get(`${API_URL}/appointments`,  { params: { hospital_id: HOSPITAL_ID, date: todayStr }, headers })
          .catch(() => ({ data: { count: 0 } })),
      ]);
      const patientsData = parseResponse(patientsRes);
      const notifsData   = parseResponse(notifsRes);
      const apptsData    = parseResponse(apptsRes);
      setStats(prev => ({
        ...prev,
        patients:      patientsData.count       || 0,
        notifications: notifsData.count         || 0,
        appointments:  apptsData.count          || 0,
      }));
      setNotifications(notifsData.notifications || []);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [HOSPITAL_ID]);

  useEffect(() => {
    if (!HOSPITAL_ID) return;
    fetchData();
  }, [HOSPITAL_ID, fetchData]);

  if (!HOSPITAL_ID) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#f87171', fontSize: 14 }}>
        No hospital assigned to your account. Contact your administrator.
      </div>
    );
  }

  const sent    = notifications.filter(n => n.status === 'sent').length;
  const pending = notifications.filter(n => n.status === 'pending').length;
  const failed  = notifications.filter(n => n.status === 'failed').length;

  return (
    <div>
      {/* Welcome */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: 0.2 }}>
          Welcome back, {user?.name || 'User'} 👋
        </div>
        <div style={{ fontSize: 13, color: '#475569', marginTop: 5 }}>
          <span style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--accent-light)', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>
            {user?.role?.replace('-', ' ')}
          </span>
          &nbsp;&nbsp;·&nbsp;&nbsp;{HOSPITAL_ID}
        </div>
      </div>

      {/* Stats */}
      <div className="grid-4" style={{ marginBottom: 28 }}>
        {STATS_CONFIG.map(s => (
          <StatCard key={s.key} label={s.label} value={stats[s.key]} grad={s.grad} icon={s.icon} loading={loading} />
        ))}
      </div>

      {/* Recent Notifications */}
      <div className="page-card" style={{ marginBottom: 24 }}>
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Recent Notifications</div>
          <span onClick={() => navigate('/notifications')} style={{ fontSize: 11, color: 'var(--accent-light)', cursor: 'pointer', fontWeight: 600 }}>
            View all →
          </span>
        </div>
        {notifications.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#334155', fontSize: 13 }}>No notifications yet</div>
        ) : (
          <div className="table-wrap">
            <table className="dark-table">
              <thead>
                <tr>
                  {['Phone', 'Type', 'Scheduled', 'Status'].map(h => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {notifications.slice(0, 5).map((n, i) => (
                  <NotificationRow key={n.notification_id || i} item={n} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Notification status overview */}
      <div className="page-card" style={{ padding: '20px 24px' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 20 }}>Notification Status Overview</div>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          {[
            { label: 'Total',   value: stats.notifications, color: 'var(--accent-light)', pct: 100 },
            { label: 'Sent',    value: sent,    color: '#4ade80', pct: stats.notifications > 0 ? (sent/stats.notifications)*100 : 0 },
            { label: 'Pending', value: pending, color: '#fbbf24', pct: stats.notifications > 0 ? (pending/stats.notifications)*100 : 0 },
            { label: 'Failed',  value: failed,  color: '#f87171', pct: stats.notifications > 0 ? (failed/stats.notifications)*100 : 0 },
          ].map((s, i) => (
            <div key={i} style={{ minWidth: 80 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color, lineHeight: 1 }}>{loading ? '—' : s.value}</div>
              <div style={{ fontSize: 11, color: '#475569', fontWeight: 500, margin: '6px 0 8px' }}>{s.label}</div>
              <div style={{ height: 4, width: 70, borderRadius: 4, background: 'var(--progress-track)' }}>
                <div style={{ height: '100%', borderRadius: 4, background: s.color, width: `${Math.min(s.pct, 100)}%`, transition: 'width 0.6s ease' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
